// Considered: no existing component walks an id-addressed A2UI tree with a message loop — nothing to
// extend. See spec Reuse Audit, specs/space-composition.md § Design System Reuse Audit.
import React, { forwardRef, useMemo } from 'react';
import type { SpaceDocument } from 'types/space';
import { CATALOG, type ComponentEntry } from '@center-platform/component-catalog';
import type { CompositionResolver } from '@center-platform/space-format/expand';
import CompositionHost, { type CompositionHostHandle } from '../../../composition-host/CompositionHost';
import type { CompositionEnvironment, CompositionHostMode, SelectedNode, SpaceAction, SpaceHostMessage } from '../../../composition-host/types';
import type { SpaceValidationError } from 'types/space';
import './SpaceRendererOrganism.scss';

// `CompositionHost` calls `getAdapter`, which ensures the built-in adapters are registered on
// first lookup — no eager module-scope call needed (see `adapterRegistry.ts`'s `ensureBuiltIns`).

const SPACE_CATALOG_ENTRIES: ComponentEntry[] = CATALOG.filter((entry) => entry.surfaces.includes('space'));

export interface SpaceRendererOrganismProps {
  document: SpaceDocument;
  mode?: CompositionHostMode;
  env?: CompositionEnvironment;
  resolveComposition?: CompositionResolver;
  onAction?: (action: SpaceAction) => void;
  onSelect?: (node: SelectedNode | null) => void;
  onExpansionErrors?: (errors: SpaceValidationError[]) => void;
  className?: string;
}

/**
 * The first host consuming `CompositionHost` — mounts a full `SpaceDocument` (the degenerate,
 * single-`createSurface`-message case of the v1.0 message loop) and renders it via the adapter
 * registry. Later hosts (focus tab, chat) reuse `CompositionHost` directly with their own message
 * streams; this organism is Space-specific only in how it derives the initial message.
 */
const SpaceRendererOrganism = forwardRef<CompositionHostHandle, SpaceRendererOrganismProps>(
  ({ document, mode = 'live', env = {}, resolveComposition, onAction, onSelect, onExpansionErrors, className }, ref) => {
    const messages: SpaceHostMessage[] = useMemo(() => {
      // Layout-relevant `settings` (e.g. `columns`) reach the root component as a render-time
      // prop merge, never a rewrite of the stored document — the conversion migration that
      // produced it is immutable (spec § Phase R3 task 2). An explicit prop already authored on
      // the root component wins over the document-level setting.
      const columns = document.settings?.columns;
      const components =
        columns === undefined
          ? document.a2ui.components
          : document.a2ui.components.map((entry) =>
              entry.id === document.a2ui.root && entry.columns === undefined ? { ...entry, columns } : entry
            );

      return [
        {
          type: 'createSurface',
          surfaceId: document.surface.componentId,
          components,
          root: document.a2ui.root,
          dataModel: document.a2ui.dataModel,
        },
      ];
    }, [document]);

    return (
      <div className={['space-renderer-organism', className].filter(Boolean).join(' ')} data-surface-family={document.surface.family} data-surface-layout={document.surface.layout}>
        <CompositionHost
          ref={ref}
          messages={messages}
          surfaceId={document.surface.componentId}
          mode={mode}
          env={env}
          resolveComposition={resolveComposition}
          catalogEntries={SPACE_CATALOG_ENTRIES}
          onAction={onAction}
          onSelect={onSelect}
          onExpansionErrors={onExpansionErrors}
        />
      </div>
    );
  }
);

SpaceRendererOrganism.displayName = 'SpaceRendererOrganism';

export default SpaceRendererOrganism;
